/**
 * Edit form for a visio background.
 */
import * as React from 'react';
import { FileText, ImageIcon } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Input } from '@/components/forms/Input';
import { FileDropzone } from '@/components/common/FileDropzone';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { useFormValidation } from '@/hooks/useFormValidation';
import { visioBackgroundEditValidationSchema } from '@/utils/validation-schemas-utils';
import { getVisioBackground, updateVisioBackground } from '@/services/visio-backgrounds.service';
import type { EditVisioBackgroundFormState } from '@/types/visio-background.types';
import {
  VISIO_BACKGROUND_ACCEPTED_FORMATS,
  VISIO_BACKGROUND_FORM_QUERY_KEYS,
  VISIO_BACKGROUND_MAX_SIZE_BYTES,
} from './visio-background-configs';
import {
  applyVisioBackgroundEditFile,
  applyVisioBackgroundName,
  buildUpdateVisioBackgroundDto,
  emptyEditVisioBackgroundForm,
  visioBackgroundToForm,
} from './visio-background-form-utils';

/** Props for {@link EditVisioBackgroundView}. */
interface EditVisioBackgroundViewProps {
  backgroundId: string;
  onClose: () => void;
  onSuccess: () => void;
}

/** Edit form for a visio background. Loads `GET /visio-backgrounds/:id` before showing fields. */
export const EditVisioBackgroundView: React.FC<Readonly<EditVisioBackgroundViewProps>> = ({
  backgroundId,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = React.useState<EditVisioBackgroundFormState>(
    emptyEditVisioBackgroundForm,
  );
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const validation = useFormValidation(visioBackgroundEditValidationSchema);
  const formRef = React.useRef(formData);
  formRef.current = formData;

  const {
    data: background,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: VISIO_BACKGROUND_FORM_QUERY_KEYS.one(backgroundId),
    queryFn: () => getVisioBackground(backgroundId),
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError,
    error,
    catalogKey: 'visioBackgroundNotFound',
    onClose,
    entityId: backgroundId,
  });

  React.useEffect(() => {
    if (!background) return;
    const next = visioBackgroundToForm(background);
    formRef.current = next;
    setFormData(next);
    validation.validateInitialFormData(next);
  }, [background, validation.validateInitialFormData]);

  React.useEffect(() => {
    if (!formData.file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(formData.file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [formData.file]);

  const updateMutation = useMutation({
    mutationFn: () =>
      updateVisioBackground(backgroundId, buildUpdateVisioBackgroundDto(formData), formData.file),
    onSuccess: () => {
      toast.success('Arrière-plan modifié avec succès !');
      onSuccess();
      onClose();
    },
    onError: (submitError: unknown) => {
      toast.error(getErrorMessage(submitError));
    },
  });

  const existingImageUrl = formData.hasExistingImage ? background?.imageUrl : undefined;
  const isValid = validation.isFormValid();

  /** Validate the name, then PATCH the name and the replacement image when one was picked. */
  const handleSubmit = () => {
    if (!background || !validation.validateForm(formData)) return;
    updateMutation.mutate();
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updated = applyVisioBackgroundName(formRef.current, e.target.value);
    formRef.current = updated;
    setFormData(updated);
    validation.validateField('name', updated.name, updated);
    validation.markAsTouched('name');
    validation.markAsDirty('name');
  };

  const handleNameBlur = () => {
    validation.markAsTouched('name');
  };

  const syncFile = (next: EditVisioBackgroundFormState) => {
    formRef.current = next;
    setFormData(next);
    validation.validateField('file', next.file, next);
    validation.markAsTouched('file');
  };

  const handleFileSelect = (selected: File | null) => {
    syncFile(applyVisioBackgroundEditFile(formRef.current, selected));
  };

  const handleFileError = (message: string | null) => {
    const next = { ...formRef.current, fileRejected: message !== null };
    formRef.current = next;
    setFormData(next);
    validation.validateField('file', next.file, next);
  };

  const handleDropzoneChange = (url: string) => {
    if (url || formData.file) return;
    const next = { ...formRef.current, file: null, hasExistingImage: false, fileRejected: false };
    formRef.current = next;
    setFormData(next);
    validation.validateField('file', next.file, next);
    validation.markAsTouched('file');
  };

  function renderImage(): React.ReactNode {
    const src = previewUrl ?? existingImageUrl;
    if (!src) return null;
    const alt = formData.name || background?.name || 'Aperçu';
    return (
      <img
        src={src}
        alt={alt}
        className="w-full aspect-video object-cover rounded-lg border border-border"
      />
    );
  }

  return (
    <SlidePanelForm
      formType="edit"
      title="Modifier l'arrière-plan"
      subtitle="Modifiez les informations de l'arrière-plan"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Enregistrer"
      isSubmitting={updateMutation.isPending}
      isValid={isValid && !!background}
      isLoading={isLoading || (!background && !isError)}
    >
      <div className="form-section">
        <h4 className="form-section-title">
          <ImageIcon className="w-4 h-4" />
          Image
        </h4>
        <div className="grid grid-cols-1 gap-4">
          <FileDropzone
            label="Fichier image"
            required
            accept={VISIO_BACKGROUND_ACCEPTED_FORMATS}
            maxSize={VISIO_BACKGROUND_MAX_SIZE_BYTES}
            value={formData.file ? undefined : existingImageUrl}
            onFileSelect={handleFileSelect}
            onChange={handleDropzoneChange}
            onError={handleFileError}
            forwardRejectedFile
            hint="JPG, PNG ou WEBP — taille maximale : 5 Mo — format paysage 16:9 recommandé"
          />
          {renderImage()}
        </div>
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <FileText className="w-4 h-4" />
          Informations générales
        </h4>
        <Input
          label="Nom"
          value={formData.name}
          onChange={handleNameChange}
          onBlur={handleNameBlur}
          placeholder="Ex: Bibliothèque"
          state={validation.getFieldState('name')}
          error={validation.getFieldError('name')}
          maxLength={120}
          required
          helperText="Nom affiché aux participants"
        />
      </div>
    </SlidePanelForm>
  );
};
